import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AvatarConfig, AvatarType } from '@/lib/avatar-config';

interface PlayerProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_type: AvatarType;
  avatar_config: AvatarConfig | null;
  avatar_url: string | null;
  elo_rating: number;
}

export const usePlayerProfiles = (playerIds: (string | null | undefined)[]) => {
  const [profiles, setProfiles] = useState<Record<string, PlayerProfile>>({});
  const [loading, setLoading] = useState(true);

  const fetchProfiles = async () => {
    // Filter out null, undefined, and empty string values
    const validPlayerIds = playerIds.filter((id): id is string => 
      id !== null && id !== undefined && id !== '' && typeof id === 'string'
    );
    
    if (validPlayerIds.length === 0) {
      setProfiles({});
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, display_name, avatar_type, avatar_config, avatar_url, elo_rating')
        .in('id', validPlayerIds);

      if (error) {
        console.error('Error fetching player profiles:', error);
        return;
      }

      const profilesMap: Record<string, PlayerProfile> = {};
      data?.forEach(profile => {
        profilesMap[profile.id] = {
          id: profile.id,
          username: profile.username,
          display_name: profile.display_name,
          avatar_type: (profile.avatar_type as AvatarType) || 'generated',
          avatar_config: profile.avatar_config ? (profile.avatar_config as unknown as AvatarConfig) : null,
          avatar_url: profile.avatar_url,
          elo_rating: profile.elo_rating,
        };
      });

      setProfiles(profilesMap);
    } catch (error) {
      console.error('Error fetching player profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [JSON.stringify(playerIds)]);

  return { profiles, loading, refetch: fetchProfiles };
};